import { createContext, useContext, useMemo, type ReactNode } from "react";
import { en } from "./en";
import { zh } from "./zh";
import type { Locale, Messages, MessageKey } from "./types";

const defaults: Record<Locale, Messages> = { zh, en };

interface I18nContextValue {
  locale: Locale;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue>({
  locale: "zh",
  t: (key) => key,
});

interface I18nProviderProps {
  locale?: Locale;
  messages?: Partial<Messages>;
  children: ReactNode;
}

export function I18nProvider({ locale = "zh", messages, children }: I18nProviderProps) {
  const value = useMemo<I18nContextValue>(() => {
    const merged = { ...defaults[locale], ...messages };
    const t = (key: MessageKey, vars?: Record<string, string | number>) => {
      const tpl = merged[key] ?? key;
      if (!vars) return tpl;
      return tpl.replace(/\{(\w+)\}/g, (_, name) => {
        const v = vars[name];
        return v === undefined ? `{${name}}` : String(v);
      });
    };
    return { locale, t };
  }, [locale, messages]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  return useContext(I18nContext).t;
}

export function useLocale() {
  return useContext(I18nContext).locale;
}
